import React, { useEffect, useState } from "react";
import closeButton from '../../../assets/images/close.png';
import { getEstimatesByWorkType } from "../../../services/AppService";
import SanctionFormModal from "../../../components/dashboard/Prceedings/AdminSanctions/SanctionFormModal";
import ViewAdminSanctionDetailsModal from "../../../components/dashboard/Prceedings/AdminSanctions/ViewAdminSanctionDetailsModal";
import { formatCurrency } from "../../../utility/formatCurrency";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const AdminSanctionEstimatesList = () => {
    const {t}=useTranslation();
    const { workTypeId } = useParams(); // URL parameter
    const location = useLocation();
    const navigate = useNavigate();
    const [estimates, setEstimates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEstimate, setSelectedEstimate] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [estimateToView, setEstimateToView] = useState(null);

    // Pagination State
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const workTypeName = location.state?.workTypeName || "Estimates";
    const onBack = () => {
        navigate(-1);
    };


    useEffect(() => {
        fetchEstimates();
    }, [workTypeId, page, size]);

    const fetchEstimates = async () => {
        try {
            setLoading(true);
            const res = await getEstimatesByWorkType(workTypeId, page, size);
            if (res) {
                setEstimates(res.content || []);
                setTotalPages(res.totalPages || 0);
                setTotalElements(res.totalElements || 0);
            }
        } catch (error) {
            console.error("Failed to load estimates");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenSanction = (item) => {
        setSelectedEstimate(item);
        setIsFormOpen(true);
    };

    const handleOpenView = (item) => {
        setEstimateToView(item);
        setViewModalOpen(true);
    };

    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">{t('pageTitles.adminSanctionsList')}</h4>
                <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={onBack} />
            </div>

            {/* Actions Bar consistent with Agendas page */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2 text-sm">
                    <label>{t('common.show')}:</label>
                    <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} className="border rounded px-2 py-1">
                        {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <label>{t('common.entries')}</label>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full border text-sm ctable rounded-lg">
                    <thead>
                        <tr className="bg-[#a0adf1] text-white">
                            <th className="px-3 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.workName')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.location')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.workType')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.estimateCost')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.date')}</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t('tables.view')}</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t('tables.adminSanction.sanctionStatus')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="text-center py-4">{t('tables.adminSanction.loadingAdminSanctions')}</td></tr>
                        ) : (
                            estimates.map((item, idx) => (
                                <tr key={item.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}>
                                    <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                    <td className="px-3 py-2">{item.nameOfWork || "N/A"}</td>
                                    <td className="px-3 py-2">{item.districtName || "N/A"}</td>
                                    <td className="px-3 py-2">{item.workTypeName || "N/A"}</td>
                                    <td className="px-3 py-2">{formatCurrency(item.estimateCost) || "N/A"}</td>
                                    <td className="px-3 py-2">{item.date || "N/A"}</td>
                                    <td className="px-3 py-2 text-center border-r">
                                        <button
                                            className="px-3 btnImgView py-1 rounded text-xs cursor-pointer"
                                            onClick={() => handleOpenView(item)}
                                            title="View Details"
                                        ></button>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        {item.isAdminSanction ? (
                                            <span className="text-green-600 font-bold">Sanctioned</span>
                                        ) : (
                                            <button
                                                className="px-3 py-1 btnImgEdit rounded text-xs cursor-pointer"
                                                onClick={() => handleOpenSanction(item)}
                                            ></button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination matched to Agendas layout */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                <p>{t('common.showing')} {page * size + 1} to {page * size + estimates.length} {t('common.of')} {totalElements} {t('common.of')}</p>
                <div className="flex gap-2">
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>{t('common.previous')}</button>
                    {[...Array(totalPages)].map((_, i) => (
                        <button key={i} onClick={() => setPage(i)} className={`px-3 py-1 border rounded ${page === i ? "bg-gradient-to-b from-white to-[#dcdcdc] border-[#979797]" : ""}`}>
                            {i + 1}
                        </button>
                    ))}
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={page === totalPages - 1}>{t('common.next')}</button>
                </div>
            </div>

            {isFormOpen && (
                <SanctionFormModal open={isFormOpen} onClose={() => setIsFormOpen(false)} planData={selectedEstimate} onSuccess={fetchEstimates} />
            )}

            <ViewAdminSanctionDetailsModal
                isOpen={viewModalOpen}
                onClose={() => { setViewModalOpen(false); setEstimateToView(null); }}
                plan={estimateToView}
            />
        </div>
    );
};

export default AdminSanctionEstimatesList;